import { UserRole, UserWithRole } from '@fsp/types';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { updateMemberRole, deleteMember } from '../../../services/team';
import { formatDate } from '@fsp/shared';
import getStatusColor from '../../../helpers/getStatusColor';
import getRoleColor from '../../../helpers/getRoleColor';
import UserAvatar from '../../../components/UserAvatar';

interface TeamMemberRowProps {
  member: UserWithRole;
  currentUserId?: string;
  organisationId: string;
  currentUserRole: UserRole;
  onMemberUpdated: (updatedMember: UserWithRole) => void;
  onMemberDeleted: (memberId: string) => void;
}

const TeamMemberRow: React.FC<TeamMemberRowProps> = ({
  member,
  organisationId,
  currentUserId,
  currentUserRole,
  onMemberUpdated,
  onMemberDeleted,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editRoleDialogOpen, setEditRoleDialogOpen] = useState(false);
  const [editRoleForm, setEditRoleForm] = useState({ role: member.role });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  // Permission checks
  const canManageTeam = currentUserRole === UserRole.OWNER || currentUserRole === UserRole.ADMIN;
  const isOwnUser = currentUserId === member.id;

  const canRemoveMember = () => {
    if (!canManageTeam) return false;
    if (isOwnUser) return false;
    if (member.role === UserRole.OWNER) return false;
    if (currentUserRole === UserRole.ADMIN && member.role === UserRole.ADMIN) return false;
    return true;
  };

  const canEditRole = () => {
    if (!canManageTeam) return false;
    if (isOwnUser) return false;
    if (member.role === UserRole.OWNER && currentUserRole !== UserRole.OWNER) return false;
    if (currentUserRole === UserRole.ADMIN && member.role === UserRole.ADMIN) return false;
    return true;
  };

  const getAvailableRoles = () => {
    const roles = [UserRole.VIEWER, UserRole.MEMBER];

    if (
      currentUserRole === UserRole.OWNER ||
      (currentUserRole === UserRole.ADMIN && member.role !== UserRole.ADMIN)
    ) {
      roles.push(UserRole.ADMIN);
    }

    if (currentUserRole === UserRole.OWNER) {
      roles.push(UserRole.OWNER);
    }

    return roles;
  };

  // Event handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditRoleOpen = () => {
    setEditRoleForm({ role: member.role });
    setEditRoleDialogOpen(true);
    setDialogError(null);
    handleMenuClose();
  };

  const handleEditRoleClose = () => {
    setEditRoleDialogOpen(false);
    setDialogError(null);
  };

  const handleDeleteOpen = () => {
    setDeleteDialogOpen(true);
    setDialogError(null);
    handleMenuClose();
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setDialogError(null);
  };

  const handleEditRoleChange = (event: SelectChangeEvent) => {
    setEditRoleForm({ role: event.target.value as UserRole });
  };

  const handleUpdateRole = async () => {
    try {
      setUpdateLoading(true);
      setDialogError(null);

      const updatedUser = await updateMemberRole(organisationId, member.id, editRoleForm.role);

      const updatedMember = {
        ...member,
        role: updatedUser.role,
        firstName: updatedUser.firstName,
        lastName: updatedUser.lastName,
        email: updatedUser.email,
        status: updatedUser.status,
        joinedAt: updatedUser.joinedAt,
      };

      onMemberUpdated(updatedMember);
      enqueueSnackbar('Member role updated successfully', { variant: 'success' });
      handleEditRoleClose();
    } catch (err: any) {
      setDialogError(err.message || 'Failed to update member role');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDeleteMember = async () => {
    try {
      setDeleteLoading(true);
      setDialogError(null);

      await deleteMember(organisationId, member.id);

      onMemberDeleted(member.id);
      enqueueSnackbar('Team member removed successfully', { variant: 'success' });
      handleDeleteClose();
    } catch (err: any) {
      setDialogError(err.message || 'Failed to remove team member');
    } finally {
      setDeleteLoading(false);
    }
  };

  const displayName =
    member.firstName && member.lastName ? `${member.firstName} ${member.lastName}` : member.email;

  return (
    <>
      <TableRow>
        <TableCell>
          <Stack direction="row" spacing={2} alignItems="center">
            <UserAvatar
              initials={
                (member.firstName?.[0] || member.email[0]).toUpperCase() +
                (member.lastName?.[0] || '').toUpperCase()
              }
              avatarUrl={member.avatarUrl || undefined}
              size="medium"
              color="primary.main"
            />

            <Box>
              <Typography variant="body1">{displayName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {member.email}
              </Typography>
            </Box>
          </Stack>
        </TableCell>
        <TableCell>
          <Chip label={member.role} size="small" color={getRoleColor(member.role)} />
        </TableCell>
        <TableCell>
          <Chip
            label={member.status}
            size="small"
            sx={{ backgroundColor: getStatusColor(member.status) }}
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {formatDate(member.joinedAt)}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {formatDate(member.lastLoginAt)}
          </Typography>
        </TableCell>
        {canManageTeam && (
          <TableCell align="right">
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              disabled={!canRemoveMember() && !canEditRole()}
            >
              <MoreVertIcon />
            </IconButton>
          </TableCell>
        )}
      </TableRow>

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {canEditRole() && (
          <MenuItem onClick={handleEditRoleOpen}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit Role
          </MenuItem>
        )}
        {canRemoveMember() && (
          <MenuItem onClick={handleDeleteOpen} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Remove
          </MenuItem>
        )}
      </Menu>

      {/* Edit Role Dialog */}
      <Dialog open={editRoleDialogOpen} onClose={handleEditRoleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Member Role</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>Update the role for {displayName}</DialogContentText>

          {dialogError && (
            <Alert severity="error" sx={{ mt: 1, mb: 2 }}>
              {dialogError}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={editRoleForm.role}
              onChange={handleEditRoleChange}
              label="Role"
              disabled={updateLoading}
            >
              {getAvailableRoles().map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditRoleClose} disabled={updateLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateRole}
            variant="contained"
            disabled={updateLoading || editRoleForm.role === member.role}
          >
            {updateLoading ? 'Updating...' : 'Update Role'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteClose}>
        <DialogTitle>Remove Team Member</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove {displayName} from the team? This action cannot be
            undone.
          </DialogContentText>

          {dialogError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {dialogError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteMember}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TeamMemberRow;
